import { useLazyQuery } from '@apollo/client';
import { Button, DatePicker, Modal, Tooltip, notification } from 'antd';
import moment from 'moment';
import { GET_RADIO_SCENARIO_EXPORT } from 'operations/queries/queryBroadcastSchedule';

import React, { useState } from 'react';
import * as XLSX from 'xlsx';

import { FileExcelOutlined } from '@ant-design/icons';

//cột theo file mẫu KB-PT-{MM}-{yyyy}.xlsx, thêm 2 cột File mp3 và MD5
const MAIN_SHEET_COLUMNS = [
  { label: 'Tên chương trình', key: 'programTitle', width: 30 },
  { label: 'Ban chủ quản', key: 'department', width: 20 },
  { label: 'Tháng', key: 'month', width: 8 },
  { label: 'Ngày', key: 'date', width: 12 },
  { label: 'Kênh', key: 'channel', width: 12 },
  { label: 'Khung giờ', key: 'timeSlot', width: 20 },
  { label: 'Giờ phát sóng', key: 'broadcastTime', width: 13 },
  { label: 'Phát chính/Phát lại', key: 'broadcastType', width: 16 },
  { label: 'Radio Title', key: 'radioTitle', width: 40 },
  { label: 'Radio Slug', key: 'radioSlug', width: 40 },
  {
    label: 'File kịch bản',
    key: 'scenarioFile',
    link: 'scenarioFile',
    width: 50
  },
  { label: 'Tên file (đã rút gọn)', key: 'scenarioFileName', width: 40 },
  { label: 'File mp3', key: 'mp3File', link: 'mp3File', width: 50 },
  { label: 'MD5', key: 'md5', width: 34 },
  { label: 'Category Slug', key: 'categorySlug', width: 30 }
];

const buildWorkbook = ({ mainData, programs, programsMissingDepartment }) => {
  const workbook = XLSX.utils.book_new();

  //sheet 1 - dữ liệu chính
  const mainRows = [
    MAIN_SHEET_COLUMNS.map(({ label }) => label),
    ...(mainData || []).map(row =>
      MAIN_SHEET_COLUMNS.map(({ key }) => row[key] ?? '')
    )
  ];
  const mainSheet = XLSX.utils.aoa_to_sheet(mainRows);
  mainSheet['!cols'] = MAIN_SHEET_COLUMNS.map(({ width }) => ({
    wch: width
  }));

  //gắn hyperlink cho các cột file
  (mainData || []).forEach((row, rowIndex) => {
    MAIN_SHEET_COLUMNS.forEach(({ link }, colIndex) => {
      const url = link && row[link];
      if (!url) return;

      const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
      if (mainSheet[cellRef]) {
        mainSheet[cellRef].l = { Target: url, Tooltip: url };
      }
    });
  });
  XLSX.utils.book_append_sheet(workbook, mainSheet, 'Dữ liệu chính');

  //sheet 2 - danh mục chương trình
  const programSheet = XLSX.utils.aoa_to_sheet([
    ['STT', 'Tên chương trình', 'Category Slug'],
    ...(programs || []).map(({ index, title, categorySlug }) => [
      index,
      title,
      categorySlug
    ])
  ]);
  programSheet['!cols'] = [{ wch: 6 }, { wch: 40 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(workbook, programSheet, 'Danh mục chương trình');

  //sheet 3 - chương trình thiếu Ban chủ quản
  const missingRows = programsMissingDepartment?.length
    ? programsMissingDepartment.map(title => [title])
    : [['Tất cả chương trình đều có Ban chủ quản']];
  const missingSheet = XLSX.utils.aoa_to_sheet([['Thông báo'], ...missingRows]);
  missingSheet['!cols'] = [{ wch: 40 }];
  XLSX.utils.book_append_sheet(workbook, missingSheet, 'Thiếu Ban chủ quản');

  return workbook;
};

const ExportRadioScenario = () => {
  const [visible, setVisible] = useState(false);
  const [monthValue, setMonthValue] = useState(moment());
  const [loading, setLoading] = useState(false);

  const [fetchExport] = useLazyQuery(GET_RADIO_SCENARIO_EXPORT, {
    fetchPolicy: 'network-only'
  });

  const onExport = async () => {
    setLoading(true);
    try {
      const { data } = await fetchExport({
        variables: { month: monthValue.month() + 1, year: monthValue.year() }
      });
      const result = data?.radioScenarioExport;

      if (!result?.mainData?.length) {
        notification.warning({
          message: 'Không có dữ liệu phát sóng trong tháng đã chọn'
        });
        return;
      }

      XLSX.writeFile(buildWorkbook(result), `${result.fileName}.xlsx`);
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Export kịch bản phát thanh theo tháng">
        <Button onClick={() => setVisible(true)} icon={<FileExcelOutlined />}>
          Export kịch bản
        </Button>
      </Tooltip>

      <Modal
        title="Export kịch bản phát thanh"
        visible={visible}
        onOk={onExport}
        onCancel={() => setVisible(false)}
        okText="Export"
        cancelText="Đóng"
        confirmLoading={loading}
        width={400}
      >
        <DatePicker
          picker="month"
          format="MM/YYYY"
          value={monthValue}
          onChange={value => setMonthValue(value || moment())}
          allowClear={false}
          style={{ width: '100%' }}
        />
      </Modal>
    </>
  );
};

export default ExportRadioScenario;
